import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { MapPin, Search, CheckCircle, AlertCircle, Loader2, AlertTriangle } from 'lucide-react';
import {
  geocodeAddressMultiple,
  validateAddressInput,
  GeocodedResult,
  AddressType
} from '../utils/routeOptimization';
import { cn } from '@/lib/utils';

interface GeocodedLocation {
  lat: number;
  lng: number;
  displayName: string;
}

interface CustomAddressDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (location: GeocodedLocation) => void;
  currentAddress?: string;
}

/**
 * Get badge styling based on address type
 */
const getTypeBadgeStyle = (type: AddressType): string => {
  switch (type) {
    case 'street_address':
      return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
    case 'street':
      return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
    case 'city':
    case 'region':
      return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
    default:
      return 'bg-muted text-muted-foreground';
  }
};

/**
 * Get human-readable label for address type
 */
const getTypeLabel = (type: AddressType): string => {
  switch (type) {
    case 'street_address':
      return 'Street Address';
    case 'street':
      return 'Street';
    case 'city':
      return 'City';
    case 'region':
      return 'Region';
    default:
      return 'Location';
  }
};

/**
 * Three-step dialog for entering and validating custom start location:
 * 1. Address entry with input validation
 * 2. Result selection from multiple options
 * 3. Confirmation prompt before applying
 */
export const CustomAddressDialog: React.FC<CustomAddressDialogProps> = ({
  open,
  onOpenChange,
  onConfirm,
  currentAddress
}) => {
  const [address, setAddress] = useState('');
  const [geocodedResults, setGeocodedResults] = useState<GeocodedResult[]>([]);
  const [selectedResult, setSelectedResult] = useState<GeocodedResult | null>(null);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [geocodeError, setGeocodeError] = useState<string | null>(null);
  const [inputError, setInputError] = useState<string | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setAddress(currentAddress || '');
      setGeocodedResults([]);
      setSelectedResult(null);
      setGeocodeError(null);
      setInputError(null);
      setShowConfirmation(false);
    }
  }, [open, currentAddress]);

  // Validate input as user types (for real-time feedback)
  const inputValidation = useMemo(() => {
    if (!address.trim()) return null;
    return validateAddressInput(address);
  }, [address]);

  const handleFindLocation = useCallback(async () => {
    const trimmed = address.trim();
    if (!trimmed) return;

    // Validate input before searching
    const validation = validateAddressInput(trimmed);
    if (!validation.valid) {
      setInputError(validation.error || 'Invalid address');
      return;
    }

    setIsGeocoding(true);
    setGeocodeError(null);
    setInputError(null);
    setGeocodedResults([]);
    setSelectedResult(null);

    try {
      const results = await geocodeAddressMultiple(trimmed, 3);

      if (results.length > 0) {
        setGeocodedResults(results);
        // Auto-select if only one result and it's high quality
        if (results.length === 1 && results[0].type === 'street_address') {
          setSelectedResult(results[0]);
        }
      } else {
        setGeocodeError('No addresses found. Try adding more details like city and state.');
      }
    } catch {
      setGeocodeError('Unable to connect to location service. Please try again.');
    }

    setIsGeocoding(false);
  }, [address]);

  const handleSelectResult = useCallback((result: GeocodedResult) => {
    setSelectedResult(result);
  }, []);

  const handleUseLocation = useCallback(() => {
    if (selectedResult) {
      setShowConfirmation(true);
    }
  }, [selectedResult]);

  const handleConfirm = useCallback(() => {
    if (selectedResult) {
      onConfirm({
        lat: selectedResult.lat,
        lng: selectedResult.lng,
        displayName: selectedResult.displayName
      });
    }
    setShowConfirmation(false);
    onOpenChange(false);
  }, [selectedResult, onConfirm, onOpenChange]);

  const handleCancel = useCallback(() => {
    setShowConfirmation(false);
    setAddress('');
    setGeocodedResults([]);
    setSelectedResult(null);
    setGeocodeError(null);
    setInputError(null);
    onOpenChange(false);
  }, [onOpenChange]);

  const handleBack = useCallback(() => {
    setShowConfirmation(false);
  }, []);

  // Truncate display name for UI
  const truncateDisplayName = (displayName: string): string => {
    const parts = displayName.split(',').map(p => p.trim());
    return parts.slice(0, 3).join(', ');
  };

  // Check if search button should be enabled
  const canSearch = address.trim().length > 0 && !isGeocoding;

  // Confirmation step
  if (showConfirmation && selectedResult) {
    const warningClasses =
      'flex items-start gap-2 mt-3 p-2 bg-amber-50 dark:bg-amber-950/30 ' +
      'rounded-md border border-amber-200 dark:border-amber-800';

    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className='sm:max-w-md'>
          <DialogHeader>
            <DialogTitle className='flex items-center gap-2'>
              <MapPin className='w-5 h-5 text-primary' />
              Confirm Starting Location
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to use this location as the starting point?
            </DialogDescription>
          </DialogHeader>

          <div className='py-4'>
            <div className='flex items-start gap-2 p-3 bg-muted rounded-lg'>
              <MapPin className='w-4 h-4 text-primary mt-0.5 flex-shrink-0' />
              <div className='flex-1'>
                <p className='text-sm font-medium'>
                  {truncateDisplayName(selectedResult.displayName)}
                </p>
                <div className='flex items-center gap-2 mt-1'>
                  <Badge
                    variant='outline'
                    className={cn('text-xs', getTypeBadgeStyle(selectedResult.type))}
                  >
                    {getTypeLabel(selectedResult.type)}
                  </Badge>
                </div>
              </div>
            </div>
            {selectedResult.warning && (
              <div className={warningClasses}>
                <AlertTriangle className='w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0' />
                <p className='text-xs text-amber-700 dark:text-amber-400'>
                  {selectedResult.warning}
                </p>
              </div>
            )}
            <p className='text-xs text-muted-foreground mt-3'>
              This will recalculate the optimized route from this new location.
            </p>
          </div>

          <DialogFooter>
            <Button variant='outline' onClick={handleBack}>
              Back
            </Button>
            <Button onClick={handleConfirm}>Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Address entry and selection step
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-xl max-h-[85vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <MapPin className='w-5 h-5 text-primary' />
            Set Custom Start Location
          </DialogTitle>
          <DialogDescription>
            Enter a complete address including street number, city, and state.
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-4 py-2'>
          {/* Address input */}
          <div className='space-y-2'>
            <div className='flex gap-2'>
              <Input
                placeholder='e.g., 123 Main St, Detroit, MI 48226'
                value={address}
                onChange={e => {
                  setAddress(e.target.value);
                  setGeocodeError(null);
                  setInputError(null);
                  // Clear results when input changes
                  if (geocodedResults.length > 0) {
                    setGeocodedResults([]);
                    setSelectedResult(null);
                  }
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleFindLocation();
                  }
                }}
                className={cn('flex-1', inputError && 'border-destructive')}
              />
              <Button
                variant='outline'
                onClick={handleFindLocation}
                disabled={!canSearch}
                className='px-3'
              >
                {isGeocoding ? (
                  <Loader2 className='w-4 h-4 animate-spin' />
                ) : (
                  <Search className='w-4 h-4' />
                )}
              </Button>
            </div>

            {/* Input validation feedback */}
            {inputError && <p className='text-sm text-destructive'>{inputError}</p>}
            {!inputError &&
              inputValidation &&
              !inputValidation.valid &&
              address.trim().length > 0 && (
                <p className='text-xs text-muted-foreground'>{inputValidation.error}</p>
              )}
            {!inputError && inputValidation?.valid && inputValidation.warning && (
              <p className='text-xs text-amber-600 dark:text-amber-400'>
                {inputValidation.warning}
              </p>
            )}
          </div>

          {/* Results list */}
          {geocodedResults.length > 0 && (
            <div className='space-y-2'>
              <p className='text-sm font-medium text-foreground'>
                {geocodedResults.length === 1
                  ? '1 result found'
                  : `${geocodedResults.length} results found - select one:`}
              </p>
              <div className='space-y-2 max-h-[240px] overflow-y-auto'>
                {geocodedResults.map(result => (
                  <button
                    type='button'
                    key={`${result.lat}-${result.lng}`}
                    onClick={() => handleSelectResult(result)}
                    className={cn(
                      'flex items-start gap-3 p-3 rounded-lg border cursor-pointer',
                      'transition-colors w-full text-left',
                      selectedResult === result
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:bg-muted/50'
                    )}
                  >
                    {/* Radio indicator */}
                    <div
                      className={cn(
                        'mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0',
                        selectedResult === result ? 'border-primary' : 'border-muted-foreground'
                      )}
                    >
                      {selectedResult === result && (
                        <div className='w-2 h-2 rounded-full bg-primary' />
                      )}
                    </div>

                    {/* Result details */}
                    <div className='flex-1 min-w-0'>
                      <p className='text-sm font-medium'>
                        {truncateDisplayName(result.displayName)}
                      </p>
                      <div className='flex items-center gap-2 mt-1.5 flex-wrap'>
                        <Badge
                          variant='outline'
                          className={cn('text-xs', getTypeBadgeStyle(result.type))}
                        >
                          {getTypeLabel(result.type)}
                        </Badge>
                        {result.addressComponents.postcode && (
                          <span className='text-xs text-muted-foreground'>
                            {result.addressComponents.postcode}
                          </span>
                        )}
                      </div>
                      {result.warning && (
                        <div className='flex items-center gap-1.5 mt-2'>
                          <AlertTriangle className='w-3 h-3 text-amber-500 flex-shrink-0' />
                          <p className='text-xs text-amber-600 dark:text-amber-400'>
                            {result.warning}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Selection indicator */}
                    {selectedResult === result && (
                      <CheckCircle className='w-4 h-4 text-primary flex-shrink-0' />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Geocode error */}
          {geocodeError && (
            <div
              className={cn(
                'flex items-start gap-2 p-3 bg-red-50 dark:bg-red-950/30',
                'rounded-md border border-red-200 dark:border-red-800'
              )}
            >
              <AlertCircle className='w-4 h-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0' />
              <p className='text-sm text-red-700 dark:text-red-400'>{geocodeError}</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleUseLocation} disabled={!selectedResult}>
            Use This Location
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CustomAddressDialog;
